import '../styles/GlobalDesign.css';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PromoCodeInput from '../components/PromoCodeInput';
import PayPalCheckout from '../components/PayPalCheckout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const PLANS = [
  { id: 'premium',     name: 'Premium',     duration: '1 Mois',    price: '3500 FCFA',  priceUSD: 6.50,  features: ['Toutes les chaînes', 'Qualité HD', '30 jours d\'accès', '1 appareil'] },
  { id: 'pro',         name: 'Pro',         duration: '3 Mois',    price: '7500 FCFA',  priceUSD: 14.00, features: ['Toutes les chaînes', 'Qualité Full HD', '3 mois d\'accès', '1 appareil'] },
  { id: 'semi_annual', name: 'Semi-Annuel', duration: '6 Mois',    price: '10500 FCFA', priceUSD: 19.00, features: ['Toutes les chaînes', 'Qualité Full HD', '6 mois d\'accès', '2 appareils'] },
  { id: 'ultimate',    name: 'Ultimate',    duration: '1 An',      price: '15500 FCFA', priceUSD: 28.00, features: ['Toutes les chaînes', 'Qualité Full HD', '1 an d\'accès', '2 appareils'], recommended: true }
];

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [paymentResult, setPaymentResult] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    nomclient: '', email: '', numeroSend: '', whatsappCode: '+221', whatsappNumber: ''
  });
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [promoCode, setPromoCode]     = useState(null);
  const [priceInfo, setPriceInfo]     = useState({ original: 0, discount: 0, final: 0 });
  const [paymentMethod, setPaymentMethod] = useState('moneyfusion');

  useEffect(() => {
    axios.get(`${API_URL}/payments/public-plans`)
      .then(({ data }) => setPlans(PLANS.filter(plan => data[plan.id]).map(plan => ({
        ...plan,
        name: data[plan.id].name,
        price: `${data[plan.id].price} FCFA`,
        priceUSD: Number(data[plan.id].priceUSD),
        duration: `${data[plan.id].duration} jours`
      }))))
      .catch(() => setError('Impossible de charger les tarifs'));
  }, []);

  /* ── Handlers ── */
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setError('');
    setPromoCode(null);
    const p = parseInt(plan.price.replace(/[^\d]/g, ''));
    setPriceInfo({ original: p, discount: 0, final: p });
    setTimeout(() => {
      document.querySelector('.payment-form-box')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handlePromoApplied = (promoData) => {
    if (promoData) {
      setPromoCode(promoData.code);
      setPriceInfo({ original: promoData.originalPrice, discount: promoData.discountAmount, final: promoData.finalPrice });
    } else {
      setPromoCode(null);
      const p = selectedPlan ? parseInt(selectedPlan.price.replace(/[^\d]/g, '')) : 0;
      setPriceInfo({ original: p, discount: 0, final: p });
    }
  };

  const validateForm = () => {
    const missingMoneyFusionPhone = paymentMethod === 'moneyfusion' && !formData.numeroSend;
    if (!formData.nomclient || !formData.email || missingMoneyFusionPhone || !formData.whatsappNumber) {
      setError('Veuillez remplir tous les champs obligatoires');
      return false;
    }
    setError('');
    return true;
  };

  /* MoneyFusion submit */
  const handleMoneyFusionSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    try {
      const body = {
        plan_type: selectedPlan.id,
        nomclient: formData.nomclient,
        email: formData.email,
        numeroSend: formData.numeroSend,
        whatsappNumber: formData.whatsappCode + formData.whatsappNumber.replace(/\s/g, '')
      };
      if (promoCode) body.promo_code = promoCode;
      const response = await axios.post(`${API_URL}/payments/initiate`, body);
      if (response.data.success) {
        localStorage.setItem('payment_token', response.data.token);
        window.location.href = response.data.paymentUrl;
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'initiation du paiement');
      setLoading(false);
    }
  };

  const createPaypalOrder = async () => {
    if (!validateForm()) throw new Error('Veuillez remplir tous les champs obligatoires');
    const body = {
        plan_type: selectedPlan.id,
        nomclient: formData.nomclient,
        email: formData.email,
        numeroSend: formData.whatsappCode + formData.whatsappNumber.replace(/\s/g, ''),
        whatsappNumber: formData.whatsappCode + formData.whatsappNumber.replace(/\s/g, ''),
        promo_code: promoCode || undefined
    };
    const response = await axios.post(`${API_URL}/payments/paypal/create-order`, body);
    return response.data.orderId;
  };

  const capturePaypalOrder = async (orderId) => {
    const response = await axios.post(`${API_URL}/payments/paypal/capture-order`, { orderId });
    return response.data;
  };

  return (
      <div className="plans-container">

        <nav className="plans-navbar">
          <div className="logo" onClick={() => window.location.href = '/'}>Fluxivo</div>
        </nav>

        <div className="plans-page-content">
          <div className="plans-page-header">
            <h1 className="plans-title">Choisissez votre plan</h1>
            <p className="plans-subtitle">Accédez à toutes les chaînes instantanément</p>
          </div>

          {paymentResult && (
            <div className="paypal-success">
              Paiement confirmé. Vos identifiants sont disponibles dans le reçu.
              {(paymentResult.receiptDownloadUrl || paymentResult.receiptUrl) && (
                <a href={paymentResult.receiptDownloadUrl || paymentResult.receiptUrl}>Télécharger le reçu</a>
              )}
            </div>
          )}

          <div className="plans-page-layout">

            {/* ── Grille des plans ── */}
            <div className="plans-page-left">
              <div className="plans-grid">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`plan-card ${selectedPlan?.id === plan.id ? 'selected' : ''} ${plan.popular ? 'popular' : ''} ${plan.recommended ? 'recommended' : ''}`}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {plan.popular     && <div className="badge">Populaire</div>}
                    {plan.recommended && <div className="badge recommended-badge">Recommandé</div>}
                    <h3 className="plan-name">{plan.name}</h3>
                    <div className="plan-price">
                      <span className="price-usd">${plan.priceUSD.toFixed(2)}</span>
                      <span className="price-main">{plan.price}</span>
                    </div>
                    <p className="plan-duration">{plan.duration}</p>
                    <ul className="plan-features">
                      {plan.features.map((f, i) => <li key={i}>✓ {f}</li>)}
                    </ul>
                    <button className={`select-button ${selectedPlan?.id === plan.id ? 'selected-button' : ''}`}>
                      {selectedPlan?.id === plan.id ? '✓ Sélectionné' : 'Sélectionner'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Formulaire de paiement ── */}
            <div className="plans-page-right">
              <div className="plans-form-sticky">
                {selectedPlan ? (
                  <div className="payment-form-box">

                    {/* En-tête plan sélectionné */}
                    <div className="payment-form-header">
                      <h2>Informations de paiement</h2>
                      <div className="selected-plan-badge">
                        {selectedPlan.name} — <span>{selectedPlan.price}</span>
                      </div>
                    </div>

                    {/* Champs communs */}
                    <div className="payment-form-fields">
                      <div className="form-group">
                        <label>Nom complet *</label>
                        <input type="text" name="nomclient" value={formData.nomclient}
                          onChange={handleInputChange} placeholder="Votre nom complet" />
                      </div>
                      <div className="form-group">
                        <label>Email *</label>
                        <input type="email" name="email" value={formData.email}
                          onChange={handleInputChange} placeholder="votre@email.com" />
                      </div>
                      {paymentMethod === 'moneyfusion' && (
                        <div className="form-group">
                          <label>Numéro Mobile Money *</label>
                          <input type="tel" name="numeroSend" value={formData.numeroSend}
                            onChange={handleInputChange} placeholder="77 123 45 67" />
                        </div>
                      )}
                      <div className="form-group">
                        <label>Numéro WhatsApp *</label>
                        <div className="whatsapp-input-group">
                          <select name="whatsappCode" value={formData.whatsappCode}
                            onChange={handleInputChange} className="country-code-select">
                            <option value="+221">🇸🇳 +221</option>
                            <option value="+223">🇲🇱 +223</option>
                            <option value="+225">🇨🇮 +225</option>
                            <option value="+226">🇧🇫 +226</option>
                            <option value="+227">🇳🇪 +227</option>
                            <option value="+228">🇹🇬 +228</option>
                            <option value="+229">🇧🇯 +229</option>
                            <option value="+224">🇬🇳 +224</option>
                            <option value="+233">🇬🇭 +233</option>
                            <option value="+234">🇳🇬 +234</option>
                            <option value="+237">🇨🇲 +237</option>
                            <option value="+241">🇬🇦 +241</option>
                            <option value="+242">🇨🇬 +242</option>
                            <option value="+243">🇨🇩 +243</option>
                            <option value="+27">🇿🇦 +27</option>
                            <option value="+33">🇫🇷 +33</option>
                            <option value="+1">🇺🇸 +1</option>
                          </select>
                          <input type="tel" name="whatsappNumber" value={formData.whatsappNumber}
                            onChange={handleInputChange} placeholder="77 123 45 67"
                            className="whatsapp-number-input" />
                        </div>
                        <small className="input-help">Pour recevoir vos identifiants</small>
                      </div>

                      {/* Code Promo */}
                      <PromoCodeInput
                        planType={selectedPlan.id}
                        planPrice={parseInt(selectedPlan.price.replace(/[^\d]/g, ''))}
                        onPromoApplied={handlePromoApplied}
                      />

                      {/* Résumé prix */}
                      {priceInfo.discount > 0 && (
                        <div className="price-summary">
                          <div className="price-line"><span>Prix original:</span><span className="original-price">{selectedPlan.price}</span></div>
                          <div className="price-line discount-line"><span>Réduction:</span><span className="discount-amount">-{priceInfo.discount} FCFA</span></div>
                          <div className="price-line total-line"><span>Total à payer:</span><span className="final-price">{priceInfo.final} FCFA</span></div>
                        </div>
                      )}

                      {error && <div className="error-message">{error}</div>}

                      {/* Toggle méthode de paiement */}
                      <div className="payment-method-toggle">
                        <button type="button"
                          className={`pm-btn ${paymentMethod === 'moneyfusion' ? 'pm-active' : ''}`}
                          onClick={() => { setPaymentMethod('moneyfusion'); setError(''); }}>
                          📱 Mobile Money
                        </button>
                        <button type="button"
                          className={`pm-btn ${paymentMethod === 'paypal' ? 'pm-active' : ''}`}
                          onClick={() => { setPaymentMethod('paypal'); setError(''); }}>
                          PayPal / Carte
                        </button>
                      </div>

                      {/* ── Section MoneyFusion ── */}
                      {paymentMethod === 'moneyfusion' && (
                        <form onSubmit={handleMoneyFusionSubmit}>
                          <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? 'Traitement...' : 'Procéder au paiement'}
                          </button>
                          <p className="payment-info">
                            🔒 Paiement sécurisé via MoneyFusion.<br />
                            Votre lien M3U sera envoyé par email immédiatement.
                          </p>
                        </form>
                      )}

                      {paymentMethod === 'paypal' && !paymentResult && (
                        <div className="paypal-section">
                          <PayPalCheckout key={selectedPlan.id} createOrder={createPaypalOrder}
                            captureOrder={capturePaypalOrder} onSuccess={setPaymentResult}
                            onError={err => setError(err.response?.data?.error || err.message || 'Paiement PayPal impossible')} />
                          <p className="payment-info">
                            Paiement sécurisé via PayPal. Les cartes disponibles dépendent de votre pays.<br />
                            Votre lien M3U sera envoyé par email immédiatement.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="payment-form-placeholder">
                    <div className="placeholder-icon">👈</div>
                    <p>Sélectionnez un plan pour continuer</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
  );
};

export default Plans;
